/**
 * Hook that bridges a Table Explorer tab's `navigationTree` field to the
 * workspace store and exposes typed push / activate / dismiss / clear actions.
 *
 * Slice 3 only: ignores branching (Slice 4) and join-collapse (Slice 5).
 *
 * Slice 1 migration: if the tab arrives with the legacy
 * `{viewMode: 'single-row', singleRowRef}` shape and no `navigationTree`,
 * we synthesize a 2-node tree on first read and persist it back into
 * `tabContent.navigationTree` (the legacy fields are not cleared — they
 * stay as a one-release read-only fallback per the plan).
 *
 * Pure helpers live in `./tabNavigationTreeHelpers` to keep this file under
 * the 200-line CLAUDE.md cap.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { migrateLegacyViewMode } from './breadcrumbTree';
import {
  buildRootTree,
  pushRowOnto,
  pushTableOnto,
  activateIfPresent,
  dismissNodeOnTree,
  selectActiveNode,
  setActiveUserFilters,
  setActiveUserSorting,
  setNodeEditMode,
  setNodePendingChanges,
  isTreeEmptyOrRoot,
  type PushRowInput,
  type PushTableInput,
  type RootInit,
} from './tabNavigationTreeHelpers';
import { useWorkspaceStore } from '../../../core/store/workspaceStore';
import { isTableExplorerTab } from '../../../core/types/workspaceTypes';
import type {
  FilterGroup,
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
  PendingChange,
  SortDefinition,
  TableExplorerTabContent,
} from '../../../core/types/workspaceTypes';

export type UseTabNavigationTreeResult = {
  /** Current tree or null if the tab has no navigation history. */
  readonly tree: NavigationTree | null;
  /** Active node (or null when no tree). */
  readonly activeNode: NavigationNode | null;
  /**
   * Ensure a root table-node exists; returns the tree. Called by entry points
   * just before pushing — the root is the current table (no FK history yet).
   */
  readonly ensureRoot: (root: RootInit) => NavigationTree;
  /** Push a row-node onto the active node and return the new id. */
  readonly pushRow: (input: PushRowInput) => NavigationNodeId;
  /** Push a table-node (filtered) onto the active node. */
  readonly pushTable: (input: PushTableInput) => NavigationNodeId;
  /** Slice 5 (EP6) — push table-node + row-node in one tree mutation; row becomes the table's child. Returns the row-node id. */
  readonly pushTableThenRow: (table: PushTableInput, row: PushRowInput) => NavigationNodeId;
  /** Activate an existing node (chip click). */
  readonly activateNode: (id: NavigationNodeId) => void;
  /** Dismiss a node + its subtree (chip ×). */
  readonly dismissNode: (id: NavigationNodeId) => void;
  /** Clear the tree entirely (back-to-grid for stack-shape callers). */
  readonly clearTree: () => void;
  /** Was the tree empty (or only-root) before the next push? */
  readonly treeWasEmpty: boolean;
  /**
   * Persist the user-added filters onto the currently-active TableNode so
   * they survive deactivation/reactivation. No-op when the active node
   * isn't a TableNode (e.g. the SRV is open).
   */
  readonly setActiveUserFilters: (userFilters: ReadonlyArray<FilterGroup>) => void;
  /** Persist the user-added sorting onto the currently-active TableNode. */
  readonly setActiveUserSorting: (userSorting: ReadonlyArray<SortDefinition>) => void;
  /**
   * Patch a TableNode's edit-mode flag. When `nodeId` isn't a TableNode (or
   * doesn't exist) the call is a no-op so callers can fire-and-forget.
   */
  readonly setNodeEditMode: (nodeId: NavigationNodeId, isEditMode: boolean) => void;
  /** Patch a TableNode's pending-changes list. Mirrors `setNodeEditMode`. */
  readonly setNodePendingChanges: (
    nodeId: NavigationNodeId,
    pendingChanges: ReadonlyArray<PendingChange>,
  ) => void;
  /**
   * Patch both `isEditMode` and `pendingChanges` on a TableNode in a single
   * tree mutation. Avoids the double-write race where two `updateTabContent`
   * calls overwrite each other within the same tick.
   */
  readonly setNodeEditState: (
    nodeId: NavigationNodeId,
    patch: { isEditMode?: boolean; pendingChanges?: ReadonlyArray<PendingChange> },
  ) => void;
};

export function useTabNavigationTree(tabId: string): UseTabNavigationTreeResult {
  const updateTabContent = useWorkspaceStore((s) => s.updateTabContent);
  const tab = useWorkspaceStore(useCallback((s) => s.tabs.find((t) => t.id === tabId), [tabId]));
  const tabContent: TableExplorerTabContent | null =
    tab && isTableExplorerTab(tab) ? tab.content : null;

  // Auto-migrate from Slice 1 shape on first read of this tab. We only call
  // updateTabContent when an actual migration produces a tree — otherwise we
  // leave the tab as-is. The check is idempotent: once `navigationTree` is
  // set, `migrateLegacyViewMode` returns null.
  useEffect(() => {
    if (!tabContent) return;
    if (tabContent.navigationTree) return;
    const migrated = migrateLegacyViewMode({
      viewMode: tabContent.viewMode,
      singleRowRef: tabContent.singleRowRef,
      schema: tabContent.schema,
      tableName: tabContent.tableName,
      navigationTree: tabContent.navigationTree,
    });
    if (migrated) {
      updateTabContent(tabId, { navigationTree: migrated });
    }
  }, [tabContent, tabId, updateTabContent]);

  const tree = tabContent?.navigationTree ?? null;
  const activeNode = useMemo<NavigationNode | null>(() => selectActiveNode(tree), [tree]);
  const treeWasEmpty = isTreeEmptyOrRoot(tree);

  const ensureRoot = useCallback(
    (root: RootInit): NavigationTree => {
      if (tree) return tree;
      const next = buildRootTree(root);
      updateTabContent(tabId, { navigationTree: next });
      return next;
    },
    [tree, tabId, updateTabContent],
  );

  const pushRow = useCallback(
    (input: PushRowInput): NavigationNodeId => {
      const base =
        tree ??
        ensureRoot({
          schema: tabContent?.schema ?? input.schema,
          table: tabContent?.tableName ?? input.table,
          filters: [],
        });
      const { tree: next, newId } = pushRowOnto(base, input);
      updateTabContent(tabId, { navigationTree: next });
      return newId;
    },
    [tree, tabContent?.schema, tabContent?.tableName, tabId, updateTabContent, ensureRoot],
  );

  const pushTable = useCallback(
    (input: PushTableInput): NavigationNodeId => {
      const base =
        tree ??
        ensureRoot({
          schema: tabContent?.schema ?? input.schema,
          table: tabContent?.tableName ?? input.table,
          filters: [],
        });
      const { tree: next, newId } = pushTableOnto(base, input);
      updateTabContent(tabId, { navigationTree: next });
      return newId;
    },
    [tree, tabContent?.schema, tabContent?.tableName, tabId, updateTabContent, ensureRoot],
  );

  const pushTableThenRow = useCallback(
    (table: PushTableInput, row: PushRowInput): NavigationNodeId => {
      const base =
        tree ??
        ensureRoot({
          schema: tabContent?.schema ?? table.schema,
          table: tabContent?.tableName ?? table.table,
          filters: [],
        });
      // Two-step composition on the SAME captured base — the second push
      // sees the table-node from the first push as the new active node, so
      // the row-node lands as the table-node's child.
      const afterTable = pushTableOnto(base, table);
      const afterRow = pushRowOnto(afterTable.tree, row);
      updateTabContent(tabId, { navigationTree: afterRow.tree });
      return afterRow.newId;
    },
    [tree, tabContent?.schema, tabContent?.tableName, tabId, updateTabContent, ensureRoot],
  );

  const activateNode = useCallback(
    (id: NavigationNodeId) => {
      if (!tree) return;
      const next = activateIfPresent(tree, id);
      if (!next) return;
      updateTabContent(tabId, { navigationTree: next });
    },
    [tree, tabId, updateTabContent],
  );

  const dismissNode = useCallback(
    (id: NavigationNodeId) => {
      if (!tree) return;
      const next = dismissNodeOnTree(tree, id);
      // `null` means the entire tree was collapsed (root dismissed); clear it.
      updateTabContent(tabId, { navigationTree: next ?? null });
    },
    [tree, tabId, updateTabContent],
  );

  const clearTree = useCallback(() => {
    updateTabContent(tabId, { navigationTree: null });
  }, [tabId, updateTabContent]);

  const setUserFilters = useCallback(
    (userFilters: ReadonlyArray<FilterGroup>) => {
      if (!tree) return;
      const next = setActiveUserFilters(tree, userFilters);
      if (!next) return;
      updateTabContent(tabId, { navigationTree: next });
    },
    [tree, tabId, updateTabContent],
  );

  const setUserSorting = useCallback(
    (userSorting: ReadonlyArray<SortDefinition>) => {
      if (!tree) return;
      const next = setActiveUserSorting(tree, userSorting);
      if (!next) return;
      updateTabContent(tabId, { navigationTree: next });
    },
    [tree, tabId, updateTabContent],
  );

  const setNodeEditModeCb = useCallback(
    (nodeId: NavigationNodeId, isEditMode: boolean) => {
      if (!tree) return;
      const next = setNodeEditMode(tree, nodeId, isEditMode);
      if (!next) return;
      updateTabContent(tabId, { navigationTree: next });
    },
    [tree, tabId, updateTabContent],
  );

  const setNodePendingChangesCb = useCallback(
    (nodeId: NavigationNodeId, pendingChanges: ReadonlyArray<PendingChange>) => {
      if (!tree) return;
      const next = setNodePendingChanges(tree, nodeId, pendingChanges);
      if (!next) return;
      updateTabContent(tabId, { navigationTree: next });
    },
    [tree, tabId, updateTabContent],
  );

  const setNodeEditState = useCallback(
    (
      nodeId: NavigationNodeId,
      patch: { isEditMode?: boolean; pendingChanges?: ReadonlyArray<PendingChange> },
    ) => {
      if (!tree) return;
      let next: NavigationTree | null = tree;
      if (patch.isEditMode !== undefined) {
        next = setNodeEditMode(next, nodeId, patch.isEditMode);
        if (!next) return;
      }
      if (patch.pendingChanges !== undefined) {
        next = setNodePendingChanges(next, nodeId, patch.pendingChanges);
        if (!next) return;
      }
      if (next === tree) return;
      updateTabContent(tabId, { navigationTree: next });
    },
    [tree, tabId, updateTabContent],
  );

  return {
    tree,
    activeNode,
    ensureRoot,
    pushRow,
    pushTable,
    pushTableThenRow,
    activateNode,
    dismissNode,
    clearTree,
    treeWasEmpty,
    setActiveUserFilters: setUserFilters,
    setActiveUserSorting: setUserSorting,
    setNodeEditMode: setNodeEditModeCb,
    setNodePendingChanges: setNodePendingChangesCb,
    setNodeEditState,
  };
}
